import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Users, TrendingUp, ExternalLink, MoreHorizontal, Edit } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase/client';

interface ExperiencePerformance {
  experience_id: string;
  title: string;
  price: number;
  booking_count: number;
  revenue: number;
  capacity_utilization: number;
  status: string;
}

export function CreatorExperienceTable() {
  const { user } = useAuth();
  const [experiences, setExperiences] = useState<ExperiencePerformance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    console.log('CreatorExperienceTable useEffect triggered, user:', user);
    fetchExperiences();
  }, [user]);

  const fetchExperiences = async () => {
    try {
      console.log('fetchExperiences called, user:', user);
      if (!user?.id) {
        console.log('No user ID found for experiences, user:', user);
        setError('User not authenticated');
        setLoading(false);
        return;
      }

      console.log('Starting to fetch creator profile for experiences, user ID:', user.id);

      // Get creator profile ID
      const { data: creatorProfile, error: profileError } = await supabase
        .from('creator_profiles')
        .select('id')
        .eq('user_id', user.id)
        .single();

      console.log('Creator profile for experiences query result:', { creatorProfile, profileError });

      if (profileError || !creatorProfile) {
        console.log('Creator profile error or not found for experiences:', profileError);
        setError('Creator profile not found');
        setLoading(false);
        return;
      }

      console.log('Found creator profile for experiences:', creatorProfile);
      setLoading(true);

      // Get experiences with booking stats
      const { data: experiences, error: expError } = await supabase
        .from('experiences')
        .select('id, title, price, status')
        .eq('creator_profile_id', creatorProfile.id);

      if (expError) throw expError;

      // Get paid confirmed bookings for revenue calculations
      const { data: bookings, error: bookingError } = await supabase
        .from('bookings')
        .select('experience_id, total_amount, participant_count')
        .eq('payment_status', 'paid')
        .eq('status', 'confirmed')
        .in('experience_id', experiences?.map(e => e.id) || []);

      if (bookingError) throw bookingError;

      // Calculate experience performance
      const experiencePerformance = experiences?.map(exp => {
        const expBookings = bookings?.filter(b => b.experience_id === exp.id) || [];
        const revenue = expBookings.reduce((sum, b) => sum + (b.total_amount || 0), 0);
        const bookingCount = expBookings.length;
        
        return {
          experience_id: exp.id,
          title: exp.title,
          price: exp.price,
          booking_count: bookingCount,
          revenue: revenue,
          capacity_utilization: 0, // Simplified
          status: exp.status
        };
      }) || [];

      setExperiences(experiencePerformance);
    } catch (err) {
      console.error('Failed to fetch experiences:', err);
      setError(err instanceof Error ? err.message : 'Failed to load experiences');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-EU', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'text-green-400 bg-green-400/10';
      case 'pending': return 'text-yellow-400 bg-yellow-400/10';
      case 'draft': return 'text-sand-400 bg-sand-400/10';
      default: return 'text-sand-400 bg-sand-400/10';
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <p className="text-sand-400">Loading experiences...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
        <p className="text-sm text-red-500">{error}</p>
        <Button variant="outline" size="sm" onClick={fetchExperiences} className="mt-2">
          Retry
        </Button>
      </div>
    );
  }

  if (experiences.length === 0) {
    return (
      <div className="text-center py-12">
        <Calendar className="h-12 w-12 text-sand-400 mx-auto mb-4" />
        <p className="text-sand-400 mb-4">No experiences created yet</p>
        <Button size="sm" onClick={() => window.location.href = '/creator/experiences/new'}>
          Create Your First Experience
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-display text-sand-100">Experience Performance</h2>
        <Button size="sm" onClick={() => window.location.href = '/creator/experiences/new'}>
          New Experience
        </Button>
      </div>

      <div className="bg-earth-800/50 rounded-lg border border-earth-700/50 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-earth-800/80">
              <tr>
                <th className="text-left p-4 text-sm font-medium text-sand-300">Experience</th>
                <th className="text-left p-4 text-sm font-medium text-sand-300">Price</th>
                <th className="text-left p-4 text-sm font-medium text-sand-300">Bookings</th>
                <th className="text-left p-4 text-sm font-medium text-sand-300">Revenue</th>
                <th className="text-left p-4 text-sm font-medium text-sand-300">Status</th>
                <th className="text-left p-4 text-sm font-medium text-sand-300">Actions</th>
              </tr>
            </thead>
            <tbody>
              {experiences.map((experience, index) => (
                <motion.tr
                  key={experience.experience_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="border-t border-earth-700/50 hover:bg-earth-700/20"
                >
                  <td className="p-4">
                    <div>
                      <p className="text-sand-100 font-medium">{experience.title}</p>
                      <p className="text-sm text-sand-400">
                        {experience.booking_count} booking{experience.booking_count !== 1 ? 's' : ''} total
                      </p>
                    </div>
                  </td>
                  <td className="p-4">
                    <p className="text-sand-100 font-mono">{formatCurrency(experience.price)}</p>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-sand-400" />
                      <span className="text-sand-100">{experience.booking_count}</span>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-green-400" />
                      <span className="text-sand-100 font-mono">{formatCurrency(experience.revenue)}</span>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(experience.status)}`}>
                      {experience.status}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => window.location.href = `/experiences/${experience.experience_id}`}
                      >
                        <ExternalLink className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => window.location.href = `/creator/experiences/${experience.experience_id}/edit`}
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary */}
      <div className="flex items-center justify-between text-sm text-sand-400 pt-4">
        <p>
          {experiences.length} experience{experiences.length !== 1 ? 's' : ''} •{' '}
          {experiences.reduce((sum, exp) => sum + exp.booking_count, 0)} total bookings •{' '}
          {formatCurrency(experiences.reduce((sum, exp) => sum + exp.revenue, 0))} total revenue
        </p>
      </div>
    </div>
  );
} 